import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { formatMinutes, QuizBoard, Ranked, SCORE } from './quizModel';
import { GuessTiles, QUIZ_COLORS } from './Tiles';

/** The advanced view: every guess as a row, scores once the quiz closes. */

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.72rem;
  color: ${QUIZ_COLORS.ink2};

  td {
    padding: 3px 4px;
    border-top: 1px solid #f3f4f6;
    vertical-align: middle;
  }
`;

const Legend = styled.div`
  margin-top: 6px;
  font-size: 0.65rem;
  font-style: italic;
  color: ${QUIZ_COLORS.label};
`;

interface GuessTableProps {
  board: QuizBoard;
  guesses: Ranked[];
  closed: boolean;
  /** Leave row by row (last row first), then call onRewound. */
  rewind?: boolean;
  onRewound?: () => void;
}

const ROW_IN_S = 0.06;
const ROW_OUT_S = 0.04;

const GuessTable: FC<GuessTableProps> = ({ board, guesses, closed, rewind = false, onRewound }) => {
  const rows = closed ? guesses : [...guesses].sort((a, b) => a.atMs - b.atMs);
  const reduce = useReducedMotion();
  const outMs = reduce ? 0 : (rows.length * ROW_OUT_S + 0.25) * 1000;
  useEffect(() => {
    if (!rewind) return undefined;
    const t = setTimeout(() => onRewound?.(), outMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewind]);
  const tile = board.rows.length > 1 ? 13 : 17;
  return (
    <div>
      <Table>
        <tbody>
          {rows.map((g, i) => (
            <motion.tr
              key={g.id}
              style={g.isYou ? { color: 'rgb(35, 35, 35)', fontWeight: 600 } : undefined}
              initial={reduce ? false : { opacity: 0, x: -10 }}
              animate={rewind ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
              transition={{ delay: rewind ? (rows.length - 1 - i) * ROW_OUT_S : i * ROW_IN_S, duration: 0.22 }}
            >
              <td style={{ width: 28, color: QUIZ_COLORS.label }}>{closed && g.rank ? `#${g.rank}` : ''}</td>
              <td style={{ width: 92, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {g.isYou ? 'you' : g.username}
              </td>
              <td>
                <GuessTiles rows={board.rows} picks={g.picks} marks={closed ? g.marks : undefined} size={tile} />
              </td>
              <td style={{ width: 44, textAlign: 'right', color: QUIZ_COLORS.label }}>{formatMinutes(g.atMs)}</td>
              <td style={{ width: 52, textAlign: 'right', fontWeight: 600, color: closed ? 'rgb(35, 35, 35)' : QUIZ_COLORS.label }}>
                {g.isAuthor ? (
                  <span style={{ color: QUIZ_COLORS.yellow }}>author</span>
                ) : closed && g.superseded ? (
                  <span style={{ color: QUIZ_COLORS.label, fontWeight: 400 }}>{`(${g.score ?? 0})`}</span>
                ) : closed ? (
                  `${g.score ?? 0} pts`
                ) : g.attemptOf > 1 ? (
                  <span style={{ color: QUIZ_COLORS.label, fontWeight: 400 }}>{`${g.attempt} of 2`}</span>
                ) : (
                  '?'
                )}
              </td>
            </motion.tr>
          ))}
        </tbody>
      </Table>
      <Legend>
        right emoji, right box +{SCORE.exact} · right emoji, wrong box +{SCORE.misplaced} · wrong {SCORE.wrong} · empty 0.
        {closed ? ' the author plays but never ranks.' : ' scores show when the guessing closes.'}
      </Legend>
    </div>
  );
};

export default GuessTable;
